import { useState, useEffect } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginScreen    from "./screens/LoginScreen";
import Dashboard      from "./screens/Dashboard";
import ResetPasswordScreen from "./screens/ResetPasswordScreen";
import TenantScreen   from "./screens/TenantScreen";
import FestivalCalendar from "./screens/FestivalCalendar";
import DemoDisclaimerModal from "./screens/DemoDisclaimerModal";

// ── Route guard ────────────────────────────────────────────────
function AppRoutes() {
  const { user, loading } = useAuth();

  // Demo disclaimer — shown once per browser session, only AFTER a real
  // login (not on the Login screen itself). Frozen 13 Jul 2026.
  const [showDisclaimer, setShowDisclaimer] = useState(
    !sessionStorage.getItem("demoDisclaimerAccepted")
  );
  const disclaimer = showDisclaimer && (
    <DemoDisclaimerModal
      onAccept={() => {
        sessionStorage.setItem("demoDisclaimerAccepted", "true");
        setShowDisclaimer(false);
      }}
    />
  );

  // Public route — Reset Password page, accessible without login
  const isResetPasswordRoute = window.location.pathname === "/reset-password";

  // Public route — Reset Password page works without login checks
  if (isResetPasswordRoute) {
    return <ResetPasswordScreen />;
  }

  // Splash / loading screen
  if (loading) {
    return (
      <div style={{
        background: "#0f0f13", minHeight: "100vh",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexDirection: "column", gap: "12px",
        fontFamily: "'Segoe UI', Arial, sans-serif",
      }}>
        <div style={{ fontSize: "32px" }}>🏢</div>
        <div style={{ color: "#6c63ff", fontSize: "14px", fontWeight: "600" }}>Adyantam</div>
        <div style={{ color: "#555570", fontSize: "12px" }}>Loading…</div>
      </div>
    );
  }

  // Tenant member — separate dashboard
  if (user?.memberType === "tenant") {
    return (
      <>
        {disclaimer}
        <FestivalCalendar />
        <TenantScreen />
      </>
    );
  }

  // Normal app flow
  return user ? (
    <>
      {disclaimer}
      <FestivalCalendar />
      <Dashboard />
    </>
  ) : <LoginScreen />;
}

// ── Scrolling Banner ───────────────────────────────────────────
const bannerStyles = `
  @keyframes marquee {
    0%   { transform: translateX(100vw); }
    100% { transform: translateX(-100%); }
  }
  .bskr-marquee {
    display: inline-block;
    white-space: nowrap;
    animation: marquee 35s linear infinite;
  }
`;

function ScrollingBanner() {
  return (
    <>
      <style>{bannerStyles}</style>
      <div style={{
        background: "#1a1a2e",
        borderBottom: "1px solid #2e2e4e",
        overflow: "hidden",
        padding: "5px 0",
        position: "sticky",
        top: 0,
        zIndex: 9999,
        width: "100%",
      }}>
        <span className="bskr-marquee" style={{
          color: "#f0c040",
          fontSize: "12px",
          fontWeight: "600",
          fontFamily: "'Segoe UI', Arial, sans-serif",
          letterSpacing: "0.4px",
        }}>
          🏛️ &nbsp; Honesty, Transparency and Discipline are the foundation for this application &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>
      </div>
    </>
  );
}

// ── Root ───────────────────────────────────────────────────────
export default function App() {
  return (
    <AuthProvider>
      <ScrollingBanner />
      <AppRoutes />
    </AuthProvider>
  );
}
